import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import API from '../../../../services/API'

// One row per medicine+batch actually in the bag, with a quantity stepper,
// instead of three cascading dropdowns that could only ever represent one
// medicine/batch per incident (the previous version overwrote its whole
// selection every time the quantity changed).
const MedicineSelector = ({
  selectedBagID,
  drugsToRemove,
  setDrugsToRemove,
}) => {
  const [selectedBagContents, setSelectedBagContents] = useState([])
  const [search, setSearch] = useState('')
  const [rowCounts, setRowCounts] = useState({})

  useEffect(() => {
    API.get(`/medsmx/bag-contents?id=${selectedBagID}`)
      .then((res) => {
        setSelectedBagContents(res.data.contents)
      })
      .catch((e) => console.log(e))
    setRowCounts({})
  }, [selectedBagID])

  const groups = useMemo(() => {
    const map = new Map()
    selectedBagContents.forEach((item) => {
      const key = `${item.medicine._id}|${item.batchNumber}`
      if (!map.has(key)) {
        map.set(key, {
          key,
          medicine: item.medicine,
          batchNumber: item.batchNumber,
          expiry: item.expiry,
          items: [],
        })
      }
      map.get(key).items.push(item)
    })
    return [...map.values()].sort((a, b) =>
      a.medicine.title.localeCompare(b.medicine.title),
    )
  }, [selectedBagContents])

  const visibleGroups = useMemo(() => {
    if (!search.trim()) return groups
    const q = search.trim().toLowerCase()
    return groups.filter((g) =>
      `${g.medicine.title} ${g.medicine.constitution ?? ''}`
        .toLowerCase()
        .includes(q),
    )
  }, [groups, search])

  useEffect(() => {
    const selected = groups.flatMap((g) => {
      const count = Math.min(rowCounts[g.key] || 0, g.items.length)
      return g.items.slice(0, count)
    })
    setDrugsToRemove(selected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowCounts, groups])

  const setCount = (key, value, max) => {
    setRowCounts((prev) => ({
      ...prev,
      [key]: Math.max(0, Math.min(max, value)),
    }))
  }

  const totalSelected = drugsToRemove.length

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <TextField
        size="small"
        label="Search medicine"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Medicine</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Expiry</TableCell>
            <TableCell align="right">Available</TableCell>
            <TableCell align="right">Remove</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleGroups.map((g) => {
            const count = rowCounts[g.key] || 0
            return (
              <TableRow key={g.key}>
                <TableCell>
                  <Typography fontWeight={600}>{g.medicine.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.medicine.constitution} {g.medicine.presentation}
                  </Typography>
                </TableCell>
                <TableCell>{g.batchNumber}</TableCell>
                <TableCell>{moment(g.expiry).format('DD/MM/YYYY')}</TableCell>
                <TableCell align="right">{g.items.length}</TableCell>
                <TableCell align="right">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap={0.5}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setCount(g.key, count - 1, g.items.length)}
                      disabled={count <= 0}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography minWidth={24} textAlign="center">
                      {count}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setCount(g.key, count + 1, g.items.length)}
                      disabled={count >= g.items.length}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            )
          })}
          {visibleGroups.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography color="text.secondary" textAlign="center">
                  No medicines match your search.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Typography
        variant="caption"
        color={totalSelected ? 'error' : 'text.secondary'}
        sx={{ fontWeight: totalSelected ? 800 : 100 }}
      >
        {totalSelected > 0
          ? `${totalSelected} item(s) will be removed from the bag - i.e. due to accidental destruction.`
          : 'Set a quantity above for anything that needs removing from the bag i.e. due to accidental destruction.'}
      </Typography>
    </Box>
  )
}

export default MedicineSelector
